import { LandingBlock } from '../types'

/** Miniatura por defecto del vídeo principal. */
export const AVA_VIDEO_THUMBNAIL =
  'https://cdn.magicpatterns.com/patterns/generated-images/b635f2a0-42cc-473c-b571-7ddd6812e6c9.jpg'

/**
 * Textos compartidos por las plantillas de landing.
 * Todo es editable después bloque a bloque desde el constructor.
 */
export const AVA_COPY = {
  // La landing oficial arranca con el logotipo y el titular: la etiqueta y la
  // línea de marca quedan vacías (editables) para respetar esa composición.
  eyebrow: '',
  brandLine: '',
  title:
    'Cómo ayudé a pasar de\n100.000 € a 200.000 € al mes\nen este negocio de cocinas',
  highlight: '100.000 € a 200.000 € al mes',
  subtitle:
    'En solo 15 días implementamos un sistema diseñado para empresas que quieren crecer sin depender del boca a boca.',
  subtitleHighlight: '15 días',
  ctaLabel: 'Quiero implementar el sistema AVA',
  videoOverlay: '',
  videoOverlayHighlight: '',
  videoNote: 'Descubre cómo funciona nuestro sistema',
  phrase:
    'Diseñado para empresas que quieren crecer\nsin depender del boca a boca',
  calendarTitle: 'Reserva tu consultoría',
  calendarText:
    'Selecciona el día y la hora que mejor te venga para conocer cómo Sistema AVA puede ayudarte.',
  calendarCta: 'Reservar hora',
  formTitle: '',
  formSubtitle: '',
  footerText: '© 2026 Sistema AVA. Atracción, Viralidad y Automatización.',
} as const

/** Franja de confianza bajo el formulario. Textos prudentes y editables. */
export const AVA_TRUST_ITEMS = [
  { icon: 'ShieldCheck', text: 'Datos 100 % seguros' },
  { icon: 'Zap', text: 'Implementación rápida' },
  { icon: 'BadgeCheck', text: 'Sistema probado' },
  { icon: 'Headphones', text: 'Soporte experto' },
]

/**
 * Identificación estable de las plantillas oficiales. Sirve para saber si una
 * landing sigue usando la estructura predeterminada y para que las
 * sincronizaciones futuras no vuelvan a activar bloques ocultos.
 */
export const AVA_TEMPLATE_KEY = 'ava_vsl_form'
export const AVA_TEMPLATE_KEY_DIRECT = 'ava_direct_form'
export const AVA_TEMPLATE_VERSION = 2

/** Identificador estable de cada plantilla oficial. */
export function templateKeyFor(template: LandingTemplateId): string {
  return template === 'formulario' ||
    template === 'solo_formulario' ||
    template === 'whatsapp'
    ? AVA_TEMPLATE_KEY_DIRECT
    : AVA_TEMPLATE_KEY
}

/**
 * Bloques que NO forman parte de la landing original y que, por tanto, se
 * crean ocultos. Siguen disponibles en la biblioteca para añadirlos a mano.
 */
export const OPTIONAL_BLOCK_TYPES = [
  'beneficios',
  'testimonios',
  'faq',
  'calendario',
  'frase',
] as const

/**
 * Bloques que cada versión de plantilla oculta UNA sola vez al migrar. Así una
 * versión nueva no vuelve a ocultar lo que el usuario decidió mostrar en una
 * versión anterior.
 */
export const HIDE_ON_TEMPLATE_VERSION: Record<number, readonly string[]> = {
  1: ['beneficios', 'testimonios', 'faq', 'calendario'],
  2: ['frase'],
}

/** Sección de tres columnas: Atraemos · Viralizamos · Automatizamos. */
export const AVA_PILLARS = [
  {
    icon: 'Magnet',
    title: 'Atraemos',
    text: 'Aplicamos un sistema de captación que genera un flujo constante de potenciales clientes para tu negocio.',
  },
  {
    icon: 'Filter',
    title: 'Viralizamos',
    text: 'Aplicamos una estrategia de contenido viral estratégico para posicionar tu marca como una autoridad y convertirte en la primera opción para tus clientes.',
  },
  {
    icon: 'TrendingUp',
    title: 'Automatizamos',
    text: 'Utilizamos inteligencia artificial para responder y calificar automáticamente a tus clientes potenciales 24/7.',
  },
]

/**
 * Solo existen DOS plantillas oficiales, ambas con la landing aprobada
 * («Cómo ayudé a pasar de 100.000 € a 200.000 € al mes…»):
 *  - `caso_exito`  → VSL + Formulario (con vídeo)
 *  - `formulario`  → la misma sin el bloque de vídeo
 * El resto de identificadores son alias históricos que apuntan a estas dos,
 * para no romper landings ya creadas.
 */
export type LandingTemplateId =
  | 'caso_exito'
  | 'vsl'
  | 'formulario'
  | 'whatsapp'
  | 'video_calendario'
  | 'con_video'
  | 'solo_formulario'

export interface LandingTemplate {
  id: LandingTemplateId
  name: string
  description: string
}

export const LANDING_TEMPLATES: LandingTemplate[] = [
  {
    id: 'caso_exito',
    name: 'Landing VSL + Formulario',
    description:
      'Landing oficial: logo, hero, vídeo, nota manuscrita, CTA, tres pilares, formulario multipaso y franja de confianza. Beneficios, testimonios, preguntas frecuentes y calendario vienen ocultos.',
  },
  {
    id: 'formulario',
    name: 'Landing Formulario Directo',
    description:
      'Mismo diseño, colores, tipografía, branding, franja de confianza y footer. Sin vídeo: logo, título, subtítulo y formulario.',
  },
]

/** Cabecera minimalista con el logotipo. */
function topbar(
  id: string,
  ctaLabel: string,
  logoAlign: 'left' | 'center' = 'center',
): LandingBlock {
  return {
    id,
    type: 'topbar',
    visible: true,
    spacing: 'normal',
    config: {
      ctaLabel,
      sticky: true,
      showLogo: true,
      // Interruptor del acceso «Iniciar sesión».
      showAuth: true,
      logoHeight: 34,
      logoAlign,
    },
  }
}

function hero(id: string): LandingBlock {
  return {
    id,
    type: 'hero',
    visible: true,
    spacing: 'amplio',
    config: {
      showLogo: false,
      eyebrow: AVA_COPY.eyebrow,
      brandLine: AVA_COPY.brandLine,
      title: AVA_COPY.title,
      highlight: AVA_COPY.highlight,
      subtitle: AVA_COPY.subtitle,
      subtitleHighlight: AVA_COPY.subtitleHighlight,
      extraLine: '',
    },
  }
}

function video(id: string, ctaLabel: string, visible = true): LandingBlock {
  return {
    id,
    type: 'video',
    visible,
    spacing: 'normal',
    config: {
      url: '',
      thumbnailUrl: AVA_VIDEO_THUMBNAIL,
      overlayTitle: AVA_COPY.videoOverlay,
      overlayHighlight: AVA_COPY.videoOverlayHighlight,
      // Anotación manuscrita bajo el vídeo, editable desde el constructor.
      note: AVA_COPY.videoNote,
      caption: '',
      ctaLabel,
      // Acabado premium de la referencia: esquinas poco redondeadas,
      // sombra suave y ancho generoso. Todo editable por dispositivo.
      radius: 12,
      shadowStyle: 'media',
      maxWidth: 860,
    },
  }
}

/** Franja de confianza bajo el formulario. Editable y ocultable. */
export function trustBlock(id: string): LandingBlock {
  return {
    id,
    type: 'confianza',
    visible: true,
    spacing: 'compacto',
    config: { items: AVA_TRUST_ITEMS },
  }
}

/**
 * Bloque de tres columnas (Atraemos · Viralizamos · Automatizamos). No forma
 * parte de la plantilla principal, pero sigue disponible en la biblioteca de
 * bloques para añadirlo desde el constructor.
 */
export function pillarsBlock(id: string): LandingBlock {
  return {
    id,
    type: 'pilares',
    visible: true,
    spacing: 'normal',
    config: { items: AVA_PILLARS },
  }
}

/** Frase de posicionamiento, disponible en la biblioteca de bloques. */
export function phraseBlock(id: string): LandingBlock {
  return {
    id,
    type: 'frase',
    // No forma parte de la landing oficial: se crea oculta.
    visible: false,
    spacing: 'normal',
    config: { text: AVA_COPY.phrase },
  }
}

/**
 * Bloque de reserva. Viene OCULTO por defecto en todas las plantillas: solo
 * aparece cuando el usuario lo activa y añade un enlace de reserva válido.
 */
function calendario(id: string, visible = false): LandingBlock {
  return {
    id,
    type: 'calendario',
    visible,
    spacing: 'normal',
    config: {
      title: AVA_COPY.calendarTitle,
      text: AVA_COPY.calendarText,
      // 'global' usa el Google Calendar conectado en Integraciones.
      calendarSource: 'global',
      url: '',
      globalUrl: '',
      // 'boton' abre el enlace; 'incrustado' muestra el calendario dentro.
      calendarMode: 'boton',
      embedHeight: 700,
      ctaLabel: AVA_COPY.calendarCta,
    },
  }
}

/** Beneficios. No forma parte de la landing original: se crea oculto. */
function beneficios(id: string): LandingBlock {
  return {
    id,
    type: 'beneficios',
    visible: false,
    spacing: 'normal',
    config: {
      title: 'Qué consigues con el Sistema AVA',
      items: [
        {
          icon: 'Target',
          title: 'Oportunidades cada semana',
          text: 'Un flujo constante de contactos cualificados, no picos aislados.',
        },
        {
          icon: 'Video',
          title: 'Contenido que posiciona',
          text: 'Vídeos pensados para que te elijan antes de hablar contigo.',
        },
        {
          icon: 'Sparkles',
          title: 'Seguimiento automático',
          text: 'La inteligencia artificial responde y cualifica mientras tu equipo vende.',
        },
      ],
    },
  }
}

/** Testimonios. Disponible en la biblioteca, pero oculto por defecto. */
function testimonios(id: string): LandingBlock {
  return {
    id,
    type: 'testimonios',
    visible: false,
    spacing: 'normal',
    config: {
      title: 'Lo que dicen nuestros clientes',
      items: [
        {
          name: 'Carlos Martínez',
          role: 'Director general',
          company: 'Grupo Empresarial XYZ',
          result: '+38 % de reuniones',
          text: 'Pasamos de depender de recomendaciones a tener reuniones agendadas todas las semanas.',
        },
        {
          name: 'Lucía Ferrer',
          role: 'Fundadora',
          company: 'Estudio Norte',
          result: 'Sistema en 15 días',
          text: 'El contenido nos posicionó y los contactos llegan ya sabiendo quiénes somos.',
        },
        {
          name: 'Javier Ruiz',
          role: 'Director comercial',
          company: 'Cocinas Delta',
          result: 'x2 facturación',
          text: 'La automatización filtra a quien no encaja y mi equipo solo habla con quien puede comprar.',
        },
      ],
    },
  }
}

/** Preguntas frecuentes. Disponible en la biblioteca, oculto por defecto. */
function faq(id: string): LandingBlock {
  return {
    id,
    type: 'faq',
    visible: false,
    spacing: 'normal',
    config: {
      title: 'Preguntas frecuentes',
      items: [
        {
          question: '¿Cuánto tarda en estar funcionando?',
          answer:
            'En 15 días dejamos el sistema instalado: contenido, campañas y automatizaciones trabajando juntas.',
        },
        {
          question: '¿Necesito grabar contenido?',
          answer:
            'Definimos juntos el plan y te acompañamos en todo el proceso, desde el guion hasta la publicación.',
        },
        {
          question: '¿Sirve para mi sector?',
          answer:
            'Funciona en cualquier empresa con un servicio de valor medio o alto que dependa de conseguir reuniones.',
        },
      ],
    },
  }
}

function formulario(id: string): LandingBlock {
  return {
    id,
    type: 'formulario',
    visible: true,
    spacing: 'amplio',
    config: {
      title: AVA_COPY.formTitle,
      subtitle: AVA_COPY.formSubtitle,
      showProgress: 'si',
      showPrivacy: 'si',
    },
  }
}

function footer(id: string): LandingBlock {
  return {
    id,
    type: 'footer',
    visible: true,
    spacing: 'normal',
    config: {
      text: AVA_COPY.footerText,
      links: ['Política de privacidad', 'Aviso legal', 'Cookies'],
      // Sin URL configurada no se muestra ninguna red social.
      socials: [
        { network: 'instagram', url: '' },
        { network: 'youtube', url: '' },
        { network: 'linkedin', url: '' },
      ],
    },
  }
}

/**
 * Devuelve los bloques de una plantilla. El generador de ids lo aporta quien
 * llama, para que en Supabase sean UUID válidos y en la plantilla estática
 * puedan ser ids legibles.
 */
export function templateBlocks(
  template: LandingTemplateId,
  makeId: () => string,
): LandingBlock[] {
  /**
   * PLANTILLA «FORMULARIO»: idéntica a la oficial, sin el bloque de vídeo.
   * Los alias históricos sin vídeo desembocan aquí.
   */
  if (
    template === 'formulario' ||
    template === 'solo_formulario' ||
    template === 'whatsapp'
  ) {
    // Sin vídeo: logo, título, subtítulo, formulario, franja de confianza y
    // footer. Todo lo demás queda oculto en la biblioteca.
    return [
      topbar(makeId(), ''),
      hero(makeId()),
      formulario(makeId()),
      trustBlock(makeId()),
      pillarsBlock(makeId()),
      phraseBlock(makeId()),
      beneficios(makeId()),
      testimonios(makeId()),
      faq(makeId()),
      calendario(makeId()),
      footer(makeId()),
    ]
  }

  /**
   * PLANTILLA OFICIAL «VSL + FORMULARIO» — única fuente de verdad de la landing
   * pública y del constructor: «Cómo ayudé a pasar de 100.000 € a 200.000 € al
   * mes a este negocio de cocinas», vídeo, Atraemos · Viralizamos ·
   * Automatizamos, frase de posicionamiento, beneficios, formulario,
   * testimonios y FAQ. El calendario viene oculto.
   */
  return [
    topbar(makeId(), ''),
    hero(makeId()),
    video(makeId(), AVA_COPY.ctaLabel),
    pillarsBlock(makeId()),
    formulario(makeId()),
    trustBlock(makeId()),
    phraseBlock(makeId()),
    beneficios(makeId()),
    testimonios(makeId()),
    faq(makeId()),
    calendario(makeId()),
    footer(makeId()),
  ]
}
